<template>
  <div>
    <Head title="Boek aanmaken" />

    <div class="flex flex-wrap mb-8">
      <h1 class="text-3xl font-bold w-full lg:w-1/2">
        <Link class="text-accent-primary-text" href="/books">Boeken</Link>
        <span class="text-accent-primary-text font-medium"> / </span>Aanmaken
      </h1>

      <div class="w-full lg:w-1/2 pr-8 pl-4">
        <Multiselect
          ref="ccbooksSelect"
          v-model="selectedBook"
          trackBy="id"
          :valueProp="'id'"
          label="title"
          placeholder="Typ om in CultuurConnect te zoeken"
          :filterResults="false"
          :min-chars="1"
          :resolveOnLoad="false"
          :delay="500"
          :searchable="true"
          :object="true"
          :clearOnSelect="false"
          :clearOnSearch="true"
          :options="
            async function (query) {
              return await fetchBooks(query); // check JS block for implementation
            }
          "
        />
      </div>
    </div>

    <div class="bg-white rounded-md shadow overflow-hidden">
      <form @submit.prevent="store">
        <div class="flex flex-wrap -mb-8 -mr-6 p-8">
          <image-input
            :value="form.cover_url ? form.cover_url : form.cover"
            @update-cover="(new_cover) => (form.cover = new_cover)"
            :error="form.errors.cover"
            class="pb-8 pr-6 w-full lg:w-1/2"
            accept="image/*"
            label="Cover"
          />

          <text-input
            v-model="form.title"
            :error="form.errors.title"
            class="pb-8 pr-6 w-full lg:w-1/2"
            label="Titel"
          />
          <text-input
            v-model="form.isbn"
            :error="form.errors.isbn"
            class="pb-8 pr-6 w-full lg:w-1/2"
            label="ISBN"
          />

          <!-- <file-input v-model="form.cover" :error="form.errors.cover" class="pb-8 pr-6 w-full lg:w-1/2" type="file"
            accept="image/*" label="Cover" /> -->

          <text-area-input
            rows="5"
            v-model="form.description"
            :error="form.errors.description"
            class="pb-8 pr-6 w-full lg:w-1/2"
            label="Beschrijving"
          />
          <!-- <text-input v-model="form.serial" :error="form.errors.serial" class="pb-8 pr-6 w-full lg:w-1/2" label="Serial" /> -->

          <div class="pb-8 pr-6 w-full lg:w-1/2">
            <label class="form-label">Genres:</label>
            <Multiselect
              v-model="form.genres"
              :mode="'tags'"
              :searchable="true"
              :create-option="true"
              :options="genresPool"
              ref="genresSelect"
            />
            <div v-if="form.errors.genres" class="form-error">
              {{ form.errors.genres }}
            </div>
          </div>

          <div class="pb-8 pr-6 w-full lg:w-1/2">
            <label class="form-label">Categorieën:</label>
            <Multiselect
              v-model="form.categories"
              :mode="'tags'"
              :searchable="true"
              :create-option="true"
              :options="categoriesPool"
              ref="categoriesSelect"
            />
            <div v-if="form.errors.categories" class="form-error">
              {{ form.errors.categories }}
            </div>
          </div>

          <div class="pb-8 pr-6 w-full lg:w-1/2">
            <label class="form-label">Auteur(s):</label>
            <Multiselect
              v-model="form.authors"
              :mode="'tags'"
              :searchable="true"
              :create-option="true"
              :options="authorsPool"
              ref="authorsSelect"
            />
            <div v-if="form.errors.authors" class="form-error">
              {{ form.errors.authors }}
            </div>
          </div>

          <div class="pb-8 pr-6 w-full lg:w-1/2">
            <label class="form-label">Barcode(s):</label>
            <Multiselect
              v-model="form.serials"
              :mode="'tags'"
              :searchable="true"
              :create-option="true"
              :options="serialsPool"
              ref="serialsSelect"
            />

            <div v-if="form.errors.serials" class="form-error">
              {{ form.errors.serials }}
            </div>
            <div v-for="serialError in serialErrors" class="form-error">
              {{ form.errors[serialError] }}
            </div>
            <!-- <div v-if="" class="form-error">{{ form.errors.serials }}
            </div> -->
          </div>
        </div>

        <div
          class="
            flex
            items-center
            justify-end
            px-8
            py-4
            bg-gray-50
            border-t border-gray-100
          "
        >
          <loading-button
            :loading="form.processing"
            class="btn-indigo"
            type="submit"
            >Boek aanmaken</loading-button
          >
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { Head, Link } from "@inertiajs/inertia-vue3";
import Layout from "@/Shared/Layout";
import TextInput from "@/Shared/TextInput";
import TextAreaInput from "@/Shared/TextareaInput";

import FileInput from "@/Shared/FileInput";
import LoadingButton from "@/Shared/LoadingButton";
import Multiselect from "@vueform/multiselect";
import ImageInput from "@/Shared/ImageInput";

// https://github.com/vueform/multiselect

export default {
  components: {
    Head,
    Link,
    LoadingButton,
    TextInput,
    TextAreaInput,
    FileInput,
    Multiselect,
    ImageInput,
  },
  layout: Layout,
  props: {
    genres: Array,
    categories: Array,
    authors: Array,
    errors: Object,
  },
  remember: "form",
  data() {
    return {
      form: this.$inertia.form({
        cover: null,
        cover_url: null,
        isbn: "",
        title: "",
        description: "",
        genres: [],
        categories: [],
        authors: [],
        serials: [],
      }),
      selectedBook: null,
      genresPool: this.genres,
      categoriesPool: this.categories,
      authorsPool: this.authors,
      serialsPool: [],
    };
  },

  computed: {
    serialErrors() {
      return Object.keys(this.errors).filter(function (k) {
        return ~k.indexOf("serials.") ? k : false;
      });
    },
  },

  methods: {
    store() {
      this.form.post("/books");
    },

    fetchBooks: async (query) => {
      if (query) {
        let response = await axios.get(`/search?q=${query}`);
        return response.data.results.data;
      }
    },

    fetchSerials: async (frabl) => {
      let response = await axios.get(`/details?frabl=${frabl}`);
      return response.data.serials;
    },

    fillBook(option) {
      // https://github.com/vueform/multiselect/issues/209
      option.genres.forEach((genre) => {
        this.genresPool.indexOf(genre) === -1
          ? this.genresPool.push(genre)
          : "";
      });

      option.categories.forEach((category) => {
        this.categoriesPool.indexOf(category) === -1
          ? this.categoriesPool.push(category)
          : "";
      });

      option.authors.forEach((author) => {
        this.authorsPool.indexOf(author) === -1
          ? this.authorsPool.push(author)
          : "";
      });

      //retrieve serial(s)
      if (option.frabl) {
        this.fetchSerials(option.frabl).then((serials) => {
          let trimmedSerials = serials.map((serial) => {
            return serial.replace(/\s/g, "");
          });

          this.serialsPool = trimmedSerials;
          this.form.serials = trimmedSerials;
        });
      } else {
        this.serialsPool = [];
        this.form.serials = [];
      }

      this.form = { ...this.form, ...option };
    },
  },

  watch: {
    selectedBook(newBook, oldBook) {
      let book = newBook || {
        cover: null,
        isbn: "",
        title: "",
        description: "",
        genres: [],
        categories: [],
        authors: [],
        serials: [],
      };

      this.fillBook(book);
    },
  },
};
</script>

